import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Monitor, 
  Terminal, 
  Mail, 
  Calendar, 
  Music, 
  Video,
  MessageSquare,
  Plus,
  X,
  Download,
  Grid3x3,
  Keyboard,
  Eye
} from 'lucide-react';
import WebsiteContent from './chaos/WebsiteContent';
import TerminalContent from './chaos/TerminalContent';
import MailContent from './chaos/MailContent';
import DesignContent from './chaos/DesignContent';
import MusicContent from './chaos/MusicContent';
import GenericContent from './chaos/GenericContent';
import FilesContent from './chaos/FilesContent';
import ChatContent from './chaos/ChatContent';
import WebContent from './chaos/WebContent';
import EditorContent from './chaos/EditorContent';
import NotesContent from './chaos/NotesContent';
import VideoContent from './chaos/VideoContent';
import MeetingContent from './chaos/MeetingContent';
import TodosContent from './chaos/TodosContent';
import FluxFooter from './FluxFooter';

const METADATA_URL = process.env.REACT_APP_LATEST_METADATA_URL || '/updates/latest.json';
const FALLBACK_DOWNLOAD_URL = process.env.REACT_APP_DOWNLOAD_URL || 'https://fluxspaces.app/downloads/FluxSpace.dmg';

const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return null;
  const size = Number(bytes);
  if (size <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const value = size / Math.pow(1024, exponent);
  return `${value % 1 === 0 ? value : value.toFixed(1)} ${units[exponent]}`;
};

const formatReleaseDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const FluxSpacesLanding = () => {
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [autopilotPaused, setAutopilotPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [activeWindow, setActiveWindow] = useState(0);
  const [windowActivity, setWindowActivity] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState(null); // 'to-calm' or 'to-chaos'
  const toggleRef = useRef(null);
  const logoRef = useRef(null);
  const [releaseInfo, setReleaseInfo] = useState(null);
  const [metadataError, setMetadataError] = useState(null);

  // Helper function to get icon for space type
  const getSpaceIcon = (spaceName) => {
    const spaceIcons = {
      'Development': '💻',
      'Communication': '💬',
      'Media': '🎵',
      'Design': '🎨',
      'Planning': '📅',
      'Research': '🔍',
      'Analytics': '📊',
      'Creative': '✨',
      'Documentation': '📝',
      'Testing': '🧪',
      'Review': '👀',
      'Brainstorm': '💡',
      'Focus': '🎯'
    };
    
    return spaceIcons[spaceName] || '📁';
  };

  // Helper function to generate contextual windows based on space name
  const getWindowsForSpace = (spaceName) => {
    const spaceWindows = {
      'Development': ['VS Code', 'Terminal', 'Browser'],
      'Communication': ['Mail', 'Slack'],
      'Media': ['Music', 'Video'],
      'Design': ['Figma', 'Photoshop', 'Browser'],
      'Planning': ['Calendar', 'Reminders'],
      'Research': ['Safari', 'Notes'],
      'Analytics': ['Browser', 'Terminal', 'Files'],
      'Creative': ['Photoshop', 'Music'],
      'Documentation': ['Notes', 'Browser'],
      'Testing': ['Terminal', 'Browser', 'VS Code'],
      'Review': ['Browser', 'Notes'],
      'Brainstorm': ['Notes', 'Figma'],
      'Focus': ['Music', 'Calendar']
    };
    
    // Return appropriate windows or fallback to generic set
    return spaceWindows[spaceName] || ['Browser', 'Files'];
  };

  const calculateMaxSpaces = useCallback(() => {
    // Maximum 4 spaces in calm mode
    return 4;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchReleaseMetadata = async () => {
      try {
        const response = await fetch(METADATA_URL, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Failed to load release metadata (status ${response.status})`);
        }
        const data = await response.json();
        if (isMounted) {
          setReleaseInfo(data);
          setMetadataError(null);
        }
      } catch (error) {
        console.error('Failed to fetch release metadata', error);
        if (isMounted) {
          setMetadataError(error.message);
        }
      }
    };

    fetchReleaseMetadata();
    return () => {
      isMounted = false;
    };
  }, []);

  const downloadHref = useMemo(() => {
    if (releaseInfo?.latestDownloadUrl) return releaseInfo.latestDownloadUrl;
    if (releaseInfo?.dmgUrl) return releaseInfo.dmgUrl;
    return FALLBACK_DOWNLOAD_URL;
  }, [releaseInfo]);

  const releaseBadgeText = useMemo(() => {
    if (!releaseInfo?.version) return null;
    const components = [`Version ${releaseInfo.version}`];
    const size = formatFileSize(releaseInfo.fileSizeBytes);
    if (size) components.push(size);
    const date = formatReleaseDate(releaseInfo.releasedAt);
    if (date) components.push(date);
    return components.join(' • ');
  }, [releaseInfo]);


  // Initialize spaces function - must be defined before useEffect that uses it
  const initializeSpaces = useCallback((fromTransition = false) => {
    const initialSpaces = [
      { id: 1, name: 'Development', windows: getWindowsForSpace('Development'), closing: false, opening: !fromTransition },
      { id: 2, name: 'Communication', windows: getWindowsForSpace('Communication'), closing: false, opening: !fromTransition },
      { id: 3, name: 'Media', windows: getWindowsForSpace('Media'), closing: false, opening: !fromTransition },
      { id: 4, name: 'Design', windows: getWindowsForSpace('Design'), closing: false, opening: !fromTransition }
    ];
    setSpaces(initialSpaces);
  }, []);

  // Initialize spaces when toggle is turned on
  useEffect(() => {
    if (isToggleOn && spaces.length === 0) {
      initializeSpaces();
    }
  }, [isToggleOn, spaces.length, initializeSpaces]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event) => setReducedMotion(event.matches);

    setReducedMotion(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Auto-pilot functionality - close and create spaces with random delays and counts
  useEffect(() => {
    let timeout;
    
    const runAutopilotSequence = () => {
      // Don't run autopilot during transitions
      if (!isToggleOn || autopilotPaused || spaces.length === 0 || isTransitioning) return;
      
      // Step 1: Wait 3-6 seconds
      const waitBeforeClose = Math.random() * 3000 + 3000; // 3-6 seconds
      
      timeout = setTimeout(() => {
        // Step 2: Close 1-2 random spaces (with delay between if closing 2)
        setSpaces(currentSpaces => {
          if (currentSpaces.length <= 1) return currentSpaces;
          
          // Decide how many to close (1 or 2)
          const maxToClose = Math.min(2, currentSpaces.length - 1); // Never close all
          const numToClose = Math.floor(Math.random() * maxToClose) + 1; // 1 or 2
          
          // Select random indices to close
          const indicesToClose = [];
          while (indicesToClose.length < numToClose) {
            const randomIndex = Math.floor(Math.random() * currentSpaces.length);
            if (!indicesToClose.includes(randomIndex)) {
              indicesToClose.push(randomIndex);
            }
          }
          
          // Close first space immediately
          const updatedSpaces = currentSpaces.map((space, index) => 
            index === indicesToClose[0] ? { ...space, closing: true } : space
          );
          
          // If closing 2 spaces, close the second one after a delay
          if (numToClose === 2) {
            setTimeout(() => {
              setSpaces(prev => prev.map((space, index) => 
                index === indicesToClose[1] ? { ...space, closing: true } : space
              ));
            }, 800); // 800ms delay between closings
          }
          
          return updatedSpaces;
        });
        
        // Step 3: Wait 3-6 seconds before creating new spaces
        const waitBeforeCreate = Math.random() * 3000 + 3000; // 3-6 seconds
        
        timeout = setTimeout(() => {
          // Step 4: Create 1-2 new spaces (always on the right side, with delay between)
          setSpaces(prev => {
            const activeSpaces = prev.filter(s => !s.closing);
            const maxSpaces = calculateMaxSpaces();
            const availableSlots = maxSpaces - activeSpaces.length;
            
            if (availableSlots <= 0) return activeSpaces;
            
            // Decide how many to create (1 or 2, but not exceeding available slots)
            const numToCreate = Math.min(
              Math.floor(Math.random() * 2) + 1, // 1 or 2
              availableSlots
            );
            
            const spaceNames = [
              'Development', 'Design', 'Communication', 'Research', 
              'Planning', 'Analytics', 'Creative', 'Documentation',
              'Testing', 'Review', 'Brainstorm', 'Focus'
            ];
            
            // Create first space immediately
            const randomName1 = spaceNames[Math.floor(Math.random() * spaceNames.length)];
            const firstSpace = {
              id: Date.now(),
              name: randomName1,
              windows: getWindowsForSpace(randomName1),
              closing: false,
              opening: true
            };
            
            // If creating 2 spaces, add the second one after a delay
            if (numToCreate === 2) {
              setTimeout(() => {
                setSpaces(current => {
                  // Re-check the limit before adding second space
                  const currentActive = current.filter(s => !s.closing);
                  const maxSpaces = calculateMaxSpaces();
                  
                  if (currentActive.length >= maxSpaces) {
                    return current; // Don't add if at max
                  }
                  
                  const randomName2 = spaceNames[Math.floor(Math.random() * spaceNames.length)];
                  const secondSpace = {
                    id: Date.now() + 1,
                    name: randomName2,
                    windows: getWindowsForSpace(randomName2),
                    closing: false,
                    opening: true
                  };
                  return [...current, secondSpace];
                });
              }, 800); // 800ms delay between creations
            }
            
            // Add first space to the right (end of array)
            return [...activeSpaces, firstSpace];
          });
          
          // Back to step 1: Continue the sequence
          runAutopilotSequence();
        }, waitBeforeCreate);
      }, waitBeforeClose);
    };
    
    if (isToggleOn && !autopilotPaused && spaces.length > 0 && !isTransitioning) {
      runAutopilotSequence();
    }
    
    return () => clearTimeout(timeout);
  }, [isToggleOn, autopilotPaused, spaces.length, isTransitioning]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const featureCards = document.querySelectorAll('.flux-feature');
    if (!featureCards.length) return undefined;

    if (reducedMotion || typeof IntersectionObserver === 'undefined') {
      featureCards.forEach((card) => card.classList.add('flux-feature-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('flux-feature-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -15%'
      }
    );

    featureCards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [reducedMotion]);

  // Memoize chaos windows array to prevent recreation on every render - must be before useEffect
  const chaosWindows = useMemo(() => [
    { id: 1, type: 'browser', content: 'website', rotation: -2.5, size: 'large', zIndex: 8 },
    { id: 2, type: 'terminal', content: 'code', rotation: 4, size: 'medium', zIndex: 7 },
    { id: 3, type: 'mail', content: 'email', rotation: -3.5, size: 'large', zIndex: 6 },
    { id: 4, type: 'music', content: 'music', rotation: 1.5, size: 'large', zIndex: 5 },
    { id: 5, type: 'finder', content: 'files', rotation: -4.5, size: 'medium', zIndex: 4 },
    { id: 6, type: 'slack', content: 'chat', rotation: 3, size: 'large', zIndex: 3 },
    { id: 7, type: 'vscode', content: 'editor', rotation: -1, size: 'large', zIndex: 2 },
    { id: 8, type: 'notion', content: 'notes', rotation: 5, size: 'medium', zIndex: 10 },
    { id: 9, type: 'finalcut', content: 'video', rotation: -3, size: 'xlarge', zIndex: 11 },
    { id: 10, type: 'zoom', content: 'meeting', rotation: 3.5, size: 'xlarge', zIndex: 12 },
    { id: 11, type: 'reminders', content: 'todos', rotation: -1.5, size: 'medium', zIndex: 13 }
  ], []);

  // Memoize action sequences to prevent recreation
  const actionSequences = useMemo(() => ({
    browser: ['scroll', 'click', 'scroll', 'type'],
    terminal: ['type', 'execute', 'scroll'],
    mail: ['scroll', 'click', 'select', 'type'],
    music: ['scroll', 'click', 'play', 'next'],
    finder: ['click', 'scroll'],
    slack: ['scroll', 'click', 'type'],
    chrome: ['scroll', 'click', 'type'],
    vscode: ['type', 'scroll', 'click'],
    notion: ['scroll', 'click', 'type'],
    finalcut: ['play', 'scroll', 'trim'],
    zoom: ['scroll', 'unmute', 'speak', 'mute'],
    reminders: ['scroll', 'check', 'add']
  }), []);

  // Active window rotation and activity simulation in chaos mode - optimized
  useEffect(() => {
    if (isToggleOn) return; // Only in chaos mode

    let windowInterval;
    let activityTimeout;
    let actionIndex = 0;

    const performActions = () => {
      const currentWindow = chaosWindows[activeWindow];
      if (!currentWindow) return;

      const actionSequence = actionSequences[currentWindow.type] || ['click', 'scroll'];
      actionIndex = 0;

      // Perform each action in sequence
      const performNextAction = () => {
        if (actionIndex < actionSequence.length) {
          setWindowActivity(prev => ({
            ...prev,
            [currentWindow.id]: {
              action: actionSequence[actionIndex],
              timestamp: Date.now()
            }
          }));

          actionIndex++;
          activityTimeout = setTimeout(performNextAction, 2000); // Increased to 2s for better performance
        } else {
          // Clear activity and move to next window
          setTimeout(() => {
            setWindowActivity(prev => ({
              ...prev,
              [currentWindow.id]: null
            }));
            setActiveWindow(prev => (prev + 1) % chaosWindows.length);
          }, 1500);
        }
      };

      performNextAction();
    };

    // Start the action sequence
    performActions();

    // Set up interval to switch windows after all actions complete
    windowInterval = setInterval(() => {
      performActions();
    }, 10000); // Reduced frequency for better performance

    return () => {
      clearInterval(windowInterval);
      clearTimeout(activityTimeout);
    };
  }, [isToggleOn, activeWindow, chaosWindows, actionSequences]);

  const getRandomAction = (windowType) => {
    const actions = {
      browser: ['scroll', 'click', 'type'],
      terminal: ['type', 'execute'],
      mail: ['scroll', 'click', 'select'],
      music: ['next', 'play', 'scroll'],
      finder: ['click', 'scroll'],
      slack: ['type', 'scroll', 'click'],
      chrome: ['scroll', 'click', 'type'],
      vscode: ['type', 'scroll', 'click'],
      notion: ['type', 'scroll', 'click'],
      finalcut: ['play', 'trim', 'scroll']
    };
    const windowActions = actions[windowType] || ['click'];
    return windowActions[Math.floor(Math.random() * windowActions.length)];
  };

  // Handle toggle with transition
  const handleToggle = useCallback(() => {
    const newToggleState = !isToggleOn;
    
    // Set transition state FIRST
    setIsTransitioning(true);
    setTransitionDirection(newToggleState ? 'to-calm' : 'to-chaos');
    
    // If transitioning to calm, always initialize with exactly 4 spaces
    if (newToggleState) {
      initializeSpaces(true); // Pass true to indicate this is from a transition
      setAutopilotPaused(true); // Pause autopilot during transition
    }
    
    // Delay the actual toggle to allow transition classes to be applied
    setTimeout(() => {
      setIsToggleOn(newToggleState);
    }, 50); // Small delay to ensure transition classes are in DOM
    
    // End transition after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
      setTransitionDirection(null);
      // Remove opening flags after transition
      if (newToggleState) {
        setSpaces(current => current.map(space => ({ ...space, opening: false })));
      }
      // Resume autopilot after transition
      setAutopilotPaused(false);
    }, 850); // 50ms delay + 0.8s animation
  }, [isToggleOn, initializeSpaces]);

  const handleUserInteraction = useCallback(() => {
    setAutopilotPaused(true);
    setTimeout(() => setAutopilotPaused(false), 10000); // Resume after 10s
  }, []);

  const closeSpace = useCallback((id) => {
    handleUserInteraction();
    setSpaces(current => current.map(space => 
      space.id === id ? { ...space, closing: true } : space
    ));
    setTimeout(() => {
      setSpaces(current => current.filter(space => space.id !== id));
    }, 800);
  }, [handleUserInteraction]);

  const addSpace = useCallback(() => {
    handleUserInteraction();
    
    // Check if we've reached the maximum number of spaces
    const activeSpaces = spaces.filter(s => !s.closing);
    if (activeSpaces.length >= calculateMaxSpaces()) {
      return; // Don't add if at max
    }
    
    const newSpace = {
      id: Date.now(),
      name: `Space ${spaces.length + 1}`,
      windows: ['New Window'],
      closing: false,
      opening: true
    };
    setSpaces(current => [...current, newSpace]);
  }, [spaces, handleUserInteraction, calculateMaxSpaces]);

  return (
    <div className={`flux-container ${isToggleOn ? 'calm-mode' : 'chaos-mode'}`}>
      {/* Top Bar */}
      <header className="flux-topbar" aria-label="Site header">
        <div className="flux-content-container">
          <div className="flux-topbar-capsule">
            <div className="flux-topbar-left">
              <Link to="/" className="flux-logo flux-logo--topbar" id="app-badge-top">
                <img src="/logo.png" alt="FluxSpaces" className="flux-logo-image" />
                <span className="flux-logo-text">FluxSpaces</span>
              </Link>
            </div>
            <div className="flux-topbar-right">
              <Button asChild className="btn-ghost btn-topbar">
                <a
                  href={downloadHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download for macOS 11+
                </a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flux-hero-stage">
        {/* Hero / Interaction Frame */}
        {/* Note: Branding appears in Top Bar only to avoid duplication */}
        <section className="flux-hero">
        {/* Hero heading removed per single-branding guidance */}

        <div className="flux-hero-toggle-area">
          <div className="flux-giant-toggle-container">
            <button
              ref={toggleRef}
              id="hero-toggle"
              className={`flux-giant-toggle ${isToggleOn ? 'on' : 'off'}`}
              role="switch"
              aria-checked={isToggleOn}
              aria-label="FluxSpaces power"
              onClick={handleToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle();
                }
              }}
            >
              <div className="flux-giant-toggle-track">
                <div className="flux-giant-toggle-knob"></div>
              </div>
            </button>

            {/* Handwritten annotation next to toggle */}
            <span className="flux-toggle-note" aria-hidden>
              turn on calmness
            </span>
          </div>
        </div>

        <div className="flux-hero-animation">
          <div className="flux-demo">
            {/* Scaling now handled directly on flux-demo-container via CSS transform */}
            <div className="flux-demo-container">
              {/* Chaos State */}
              <div
                className={`flux-chaos ${isToggleOn ? 'hidden' : 'visible'} ${isTransitioning && transitionDirection === 'to-calm' ? 'transitioning-out' : ''} ${isTransitioning && transitionDirection === 'to-chaos' ? 'transitioning-in' : ''}`}
                style={{ display: isToggleOn && !isTransitioning ? 'none' : 'block' }}
              >
                <div 
                  className="flux-chaos-desktop" 
                  style={{
                    position: 'absolute',
                    top: '46%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '85%',
                    height: '82%',
                    background: '#1f2127',
                    border: '20px solid #3a3f4a',
                    borderRadius: '16px',
                    zIndex: 0,
                    pointerEvents: 'none'
                  }}
                />
                {chaosWindows.map((window, index) => {
                  const isActive = index === activeWindow;
                  const activity = windowActivity[window.id];

                  // Memoized style calculation
                  const windowStyle = useMemo(() => {
                    const style = {
                      '--window-rotation': `${window.rotation}deg`,
                      animationDelay: `${index * 0.15}s`
                    };

                    // Only add transform if NOT transitioning
                    if (!isTransitioning) {
                      style.transform = `rotate(${window.rotation}deg)`;
                    }

                    return style;
                  }, [isActive, isTransitioning, window.rotation, index]);

                  return (
                    <div
                      key={window.id}
                      className={`flux-chaos-window ${window.size} ${window.type}-window ${isActive ? 'active' : ''}`}
                      style={windowStyle}
                    >
                      <div className="flux-window-titlebar">
                        <div className="flux-window-controls">
                          <div className="flux-window-button close" />
                          <div className="flux-window-button minimize" />
                          <div className="flux-window-button maximize" />
                        </div>
                        <div className="flux-window-title">
                          {window.type === 'browser' && 'Safari'}
                          {window.type === 'terminal' && 'Terminal'}
                          {window.type === 'mail' && 'Mail'}
                          {window.type === 'music' && 'Music'}
                          {window.type === 'finder' && 'Finder'}
                          {window.type === 'slack' && 'Slack'}
                          {window.type === 'chrome' && 'Chrome'}
                          {window.type === 'vscode' && 'VS Code'}
                          {window.type === 'notion' && 'Notion'}
                          {window.type === 'finalcut' && 'Final Cut Pro'}
                          {window.type === 'zoom' && 'Zoom'}
                          {window.type === 'reminders' && 'Reminders'}
                        </div>
                      </div>
                      <div className="flux-window-content">
                        {window.content === 'website' && (
                          <WebsiteContent activity={activity} />
                        )}
                        {window.content === 'code' && (
                          <TerminalContent activity={activity} />
                        )}
                        {window.content === 'email' && (
                          <MailContent activity={activity} />
                        )}
                        {window.content === 'design' && (
                          <DesignContent activity={activity} />
                        )}
                        {window.content === 'music' && (
                          <MusicContent activity={activity} />
                        )}
                        {window.content === 'files' && (
                          <FilesContent />
                        )}
                        {window.content === 'chat' && (
                          <ChatContent />
                        )}
                        {window.content === 'web' && (
                          <WebContent activity={activity} />
                        )}
                        {window.content === 'editor' && (
                          <EditorContent />
                        )}
                        {window.content === 'notes' && (
                          <NotesContent />
                        )}
                        {window.content === 'video' && (
                          <VideoContent activity={activity} />
                        )}
                        {window.content === 'meeting' && (
                          <MeetingContent activity={activity} />
                        )}
                        {window.content === 'todos' && (
                          <TodosContent activity={activity} />
                        )}
                        {/* Fallback for any future/unknown content types */}
                        {!([
                          'website',
                          'code',
                          'email',
                          'design',
                          'music',
                          'files',
                          'chat',
                          'web',
                          'editor',
                          'notes',
                          'video',
                          'meeting',
                          'todos'
                        ].includes(window.content)) && <GenericContent />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Calm State */}
              <div
                className={`flux-calm ${isToggleOn ? 'visible' : 'hidden'} ${isTransitioning && transitionDirection === 'to-calm' ? 'transitioning-in' : ''} ${isTransitioning && transitionDirection === 'to-chaos' ? 'transitioning-out' : ''}`}
                style={{ display: !isToggleOn && !isTransitioning ? 'none' : 'flex' }}
              >
                {/* Straight screen */}
                <div 
                  className="flux-calm-desktop" 
                  aria-hidden 
                  style={{
                    position: 'absolute',
                    top: '46%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '85%',
                    height: '82%',
                    background: '#1f2127',
                    border: '20px solid #3a3f4a',
                    borderRadius: '16px',
                    zIndex: 0,
                    pointerEvents: 'none'
                  }}
                />
                {/* Inner screen content area (clips to inside of the bezel) */}
                <div className="flux-screen-content">
                  <div className="flux-spaces-rail">
                  {spaces.map((space, index) => (
                    <Card
                      key={space.id}
                      className={`flux-space-card ${space.closing ? 'closing' : ''} ${space.opening && !isTransitioning ? 'opening' : ''}`}
                      style={{ animationDelay: `${index * 60}ms` }}
                    >
                      <div className="flux-space-header">
                        <span className="flux-space-icon">{getSpaceIcon(space.name)}</span>
                        <input
                          type="text"
                          value={space.name}
                          onChange={(e) => {
                            if (e.target.value.length <= 25) {
                              setSpaces(current => current.map(s =>
                                s.id === space.id ? { ...s, name: e.target.value } : s
                              ));
                            }
                          }}
                          onFocus={handleUserInteraction}
                          className="flux-space-name"
                          maxLength={25}
                        />
                        <div className="flux-space-controls">
                          <button
                            className="flux-close-space"
                            onClick={() => closeSpace(space.id)}
                            aria-label="Close space"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="flux-space-windows">
                        {space.windows.map((window, winIndex) => (
                          <div key={winIndex} className="flux-space-window">
                            <div className="flux-space-window-header" />
                            <div className="flux-space-window-content">
                              <span className="flux-window-type">{window}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}

                  {spaces.filter(s => !s.closing).length < calculateMaxSpaces() && (
                    <button
                      className="flux-add-space"
                      onClick={addSpace}
                      aria-label="Add new space"
                    >
                      <Plus className="w-6 h-6" />
                    </button>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </section>
        {/* CTA + Description: cohesive content block */}
        <section className="flux-download-cta">
          <div className="flux-content-container">
            <div className="flux-cta-block">
              <div className="flux-download-actions">
                <Button asChild className="btn-primary btn-large flux-download-button">
                  <a
                    href={downloadHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="w-6 h-6 mr-3" />
                    Download for macOS 11+
                  </a>
                </Button>
                {releaseBadgeText && (
                  <p className="flux-download-meta">
                    {releaseBadgeText}
                  </p>
                )}
                {releaseInfo?.releaseNotesUrl && (
                  <a
                    href={releaseInfo.releaseNotesUrl}
                    className="flux-download-notes"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View release notes
                  </a>
                )}
                {metadataError && (
                  <p className="flux-download-meta flux-download-meta--error">
                    Unable to load live version details. Using fallback download link.
                  </p>
                )}
              </div>

              {/* Optional one-line emphasis */}
              <p className="flux-cta-intro text-balance">
                Your menu bar becomes the calm command center for every desktop space.
              </p>

              {/* Elevated explanation with better rhythm */}
              <div className="flux-cta-text text-pretty">
                <p className="text-balance">
                  In one glance, FluxSpaces <strong>visualizes your desktop spaces</strong> in the macOS menu bar{' '}
                  <span className="no-wrap-lg">— by number, icon, or custom name.</span>
                </p>
                <p className="text-pretty">
                  <strong>Dedicate each desktop to a specific purpose</strong> and <strong>maintain clarity</strong> across multiple projects. Keep your calendar and to‑dos on one space, development work on another, and video calls on a third{' '}
                  <span className="no-wrap-lg">— all without constant rearranging.</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Soft divider to connect to features */}
        <div className="flux-section-divider" aria-hidden />
      </div>

      {/* Features */}
      <section className="flux-features" id="features">
        <div className="flux-content-container">
          <div className="flux-features-grid">
            <div className="flux-feature">
              <Grid3x3 className="w-8 h-8 text-flux-accent mb-4" />
              <h3>See Spaces at a Glance</h3>
              <p>FluxSpaces shows your desktops in the macOS menu bar for quick, calm orientation.</p>
            </div>
            <div className="flux-feature">
              <Keyboard className="w-8 h-8 text-flux-accent mb-4" />
              <h3>Name or Icon per Space</h3>
              <p>Use numbers, icons, or custom names — give each desktop a clear purpose.</p>
            </div>
            <div className="flux-feature">
              <Monitor className="w-8 h-8 text-flux-accent mb-4" />
              <h3>Works Across Displays</h3>
              <p>Keeps spaces organized across multiple monitors with a minimal, native feel.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <FluxFooter />
    </div>
  );
};

export default FluxSpacesLanding;
